import { Injectable } from "@angular/core";
import { BehaviorSubject, lastValueFrom, Observable } from "rxjs";
import {
  TwoFactorControllerService,
  UserControllerService,
} from "src/app/api/services";
import {
  TOKEN_STORAGE_KEYS,
  TokenStorageService,
} from "./token-storage.service";
import { AuthRole, UserInfo } from "src/app/api/models";
import { SettingsService } from "src/app/shared/services/settings.service";

export type Verify2FAType = "code" | "recoveryCode";

export type LoginResult =
  | { status: "logged-in" }
  | { status: "2fa-required" }
  | { status: "2fa-setup-required" }
  | { status: "failed"; message?: string };

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private currentUser: BehaviorSubject<UserInfo | undefined> =
    new BehaviorSubject<UserInfo | undefined>(undefined);

  private isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  currentUser$: Observable<UserInfo | undefined> = this.currentUser;

  isLoggedIn$: Observable<boolean> = this.isLoggedIn;

  isLoginFailed: boolean = false;
  errorMessage: string = "";

  constructor(
    private ucService: UserControllerService,
    private twofaService: TwoFactorControllerService,
    private tsService: TokenStorageService,
    private settingsService: SettingsService,
  ) {
    this.currentUser.next(this.tsService.getUser());
    this.isLoggedIn.next(!!this.tsService.getToken(TOKEN_STORAGE_KEYS.AUTH));
  }

  get isLoggedInValue(): boolean {
    return this.isLoggedIn.value;
  }

  get currentUserValue(): UserInfo | undefined {
    return this.currentUser.value;
  }

  get currentUserRole(): AuthRole | undefined {
    return this.currentUser.value?.role;
  }
  async login(username: string, password: string): Promise<LoginResult> {
    const login$ = this.ucService.login({
      body: { email: username, password: password },
    });
    const response = await lastValueFrom(login$).catch((reason) => {
      console.error("Login Failed", reason);
      return false;
    });
    if (!response) {
      return { status: "failed", message: "Login failed" };
    }

    if ((response as any).token) {
      const token = (response as any).token as string;
      this.tsService.saveToken(token, TOKEN_STORAGE_KEYS.AUTH);
      await this.saveUser();
      await this.settingsService.loadGlobalSettings();
      this.isLoggedIn.next(true);
      this.isLoginFailed = false;
      return { status: "logged-in" };
    }

    if ((response as any).twoFactorRequired && (response as any).pendingToken) {
      this.tsService.saveToken(
        (response as any).pendingToken,
        TOKEN_STORAGE_KEYS.PENDING_2FA,
      );
      this.isLoggedIn.next(false);
      this.isLoginFailed = false;
      return { status: "2fa-required" };
    }

    if (
      (response as any).twoFactorSetupRequired &&
      (response as any).setupToken
    ) {
      this.tsService.saveToken(
        (response as any).setupToken,
        TOKEN_STORAGE_KEYS.PENDING_2FA_SETUP,
      );
      this.isLoggedIn.next(false);
      this.isLoginFailed = false;
      return { status: "2fa-setup-required" };
    }

    return { status: "failed", message: "Unexpected login response" };
  }

  async verify2fa(payload: {
    code?: string;
    recoveryCode?: string;
  }): Promise<LoginResult> {
    const pendingToken = this.tsService.getToken(
      TOKEN_STORAGE_KEYS.PENDING_2FA,
    );
    if (!pendingToken) {
      return { status: "failed", message: "Missing pending 2FA token" };
    }

    const { code, recoveryCode } = payload;
    const hasCode = !!code?.trim();
    const hasRecoveryCode = !!recoveryCode?.trim();

    if (hasCode && hasRecoveryCode) {
      return {
        status: "failed",
        message: "Provide code or recoveryCode, not both params together",
      };
    }

    if (!hasCode && !hasRecoveryCode) {
      return {
        status: "failed",
        message: "Provide code or recoveryCode",
      };
    }

    const response = await lastValueFrom(
      this.twofaService.verify2fa({
        authorization: `Bearer ${pendingToken}`,
        body: { code, recoveryCode },
      }),
    ).catch((reason) => {
      console.error("2FA verify failed", reason);
      return null;
    });
    if (!response?.token) {
      return { status: "failed", message: "Invalid 2FA or recovery code" };
    }
    this.tsService.saveToken(response.token, TOKEN_STORAGE_KEYS.AUTH);

    await this.saveUser();
    await this.settingsService.loadGlobalSettings();

    this.isLoggedIn.next(true);
    this.isLoginFailed = false;
    return { status: "logged-in" };
  }

  logout() {
    this.isLoggedIn.next(false);
    this.currentUser.next(undefined);
    this.tsService.clearStorage();
  }

  checkUserAccessRightByRole(role: string): boolean {
    const userRole = this.currentUser.value && this.currentUser.value.role;

    if (!userRole || userRole.id !== role) {
      return false;
    } else {
      return true;
    }
  }

  async saveUser() {
    const userToken = this.tsService.getToken(TOKEN_STORAGE_KEYS.AUTH);
    if (!userToken) {
      console.error("No user token");
      this.currentUser.next(undefined);
      return;
    }

    await this.whoAmI();
  }

  async reloadUser() {
    await this.whoAmI();
  }

  setLoggedIn(value: boolean) {
    this.isLoggedIn.next(value);
  }

  private async whoAmI() {
    const user = await lastValueFrom(this.ucService.whoAmI()).catch((e) => {
      console.error("whoAmI failed", e);
      return null;
    });

    if (!user) {
      this.currentUser.next(undefined);
      return;
    }

    this.currentUser.next(user);
    this.tsService.saveUser(user);
  }
}
